import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import productsService from '../services/productsService';
import collectionsService from '../services/collectionsService';
import { Search } from 'lucide-react';
import logger from '../utils/logging';
import analyticsService from '../services/analyticsService';
import EyeCatchingHeading from '../components/features/EyeCatchingHeading';
import PriceDisplay from '../components/features/PriceDisplay';
import PLACEHOLDER_IMAGE from '../utils/placeholderImage';
import { productDetailPath } from '../utils/productPath';
import DynamicFilters from '../components/features/DynamicFilters';
// Products come from Mango API only

// Map category display name → categories API slug
const CATEGORY_ID_MAP = {
  'Food & Beverage': 'food-beverage',
  'Apparel': 'apparel-clothing',
  'Electronics': 'electronics-computers',
  'Beauty & Personal Care': 'beauty-skincare',
  'Home & Living': 'home-garden-outdoor-bbq',
};

const Gallery = () => {
  const [products, setProducts] = useState([]);
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [searchTimeout, setSearchTimeout] = useState(null);
  // Dynamic attribute filters — keyed by fieldName, value depends on filterType
  const [activeAttrFilters, setActiveAttrFilters] = useState({});
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    logger.logComponentMount('Gallery', {
      timestamp: Date.now()
    });
  }, []);

  useEffect(() => {
    loadCollections();
  }, []);

  // Reload products whenever category or attribute filters change
  useEffect(() => {
    loadProducts();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterCategory, activeAttrFilters]);

  const loadCollections = async () => {
    logger.log('info', 'api', 'Loading collections for Gallery', { timestamp: Date.now() });
    try {
      const collectionsData = await collectionsService.getCollections();
      logger.log('info', 'system', `Collections loaded successfully`, {
        totalCollections: collectionsData.length
      });
      setCollections(collectionsData);
    } catch (error) {
      logger.logError(error, 'Gallery loadCollections');
      // Collections are optional, don't fail if they can't load
      setCollections([]);
    }
  };

  const loadProducts = useCallback(async () => {
    logger.log('info', 'api', 'Loading products for Gallery', { timestamp: Date.now() });
    try {
      setLoading(true);

      const options = { forceRefresh: true };
      if (filterCategory && filterCategory !== 'all') {
        options.category = filterCategory;
      }
      // Pass attribute filters to the API
      const attrToSend = {};
      for (const [k, v] of Object.entries(activeAttrFilters)) {
        if (v !== undefined && v !== null && v !== false &&
            !(Array.isArray(v) && v.length === 0)) {
          attrToSend[k] = v;
        }
      }
      if (Object.keys(attrToSend).length > 0) options.attr = attrToSend;

      logger.logAPIRequest('/api/v1/products/public', 'GET');
      const serverProducts = await productsService.getProducts(options);
      logger.logAPIResponse('/api/v1/products/public', 'GET', 200, { count: serverProducts.length });

      setProducts(serverProducts);
      logger.log('info', 'system', `Products loaded successfully from Mango API`, {
        totalProducts: serverProducts.length,
        source: 'Mango Business System API',
      });
    } catch (error) {
      logger.logError(error, 'Gallery loadProducts');
      console.error('Failed to load products from Mango API:', error);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterCategory, activeAttrFilters]);

  // Category + attribute filters are now server-side (via loadProducts).
  // Client-side: search text + stock availability only.
  const filteredProducts = products.filter(product => {
    const inventoryManagedProductTypes = ['physical', 'print_on_demand', 'bundle', 'rental'];
    if (
      product.trackInventory &&
      inventoryManagedProductTypes.includes((product.productType || '').toLowerCase()) &&
      product.inventoryQty !== undefined &&
      product.inventoryQty === 0
    ) {
      return false;
    }
    if (!searchTerm) return true;
    return (
      product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (product.shortDescription && product.shortDescription.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (product.description && product.description.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  });

  const filteredCollections = collections.filter(collection => {
    const matchesSearch = collection.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          (collection.shortDescription && collection.shortDescription.toLowerCase().includes(searchTerm.toLowerCase())) ||
                          (collection.description && collection.description.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesSearch;
  });

  // Combine products and collections for display, sorted by type (collections first, then products)
  const allItems = [
    ...filteredCollections.map(c => ({ type: 'collection', data: c })),
    ...filteredProducts.map(p => ({ type: 'product', data: p }))
  ];


  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'radial-gradient(ellipse at center, rgba(26, 26, 46, 0.8) 0%, rgba(22, 33, 62, 0.8) 50%, rgba(15, 15, 35, 0.9) 100%)',
        padding: window.innerWidth <= 768 ? '80px 16px 24px 16px' : '80px 24px 24px 24px',
        color: 'white'
      }}>
        <h1 style={{ textAlign: 'center', marginBottom: '32px', fontSize: '28px' }}>Gallery</h1>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: '24px',
          maxWidth: '1200px',
          margin: '0 auto'
        }}>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div
              key={i}
              style={{
                background: 'rgba(255,255,255,0.05)',
                borderRadius: '16px',
                overflow: 'hidden',
                border: '1px solid rgba(255,255,255,0.1)'
              }}
            >
              <div style={{
                height: '240px',
                background: 'linear-gradient(90deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.02) 50%, rgba(255,255,255,0.08) 100%)',
                backgroundSize: '200% 100%',
                animation: 'gallery-skeleton-shimmer 1.5s ease-in-out infinite'
              }} />
              <div style={{ padding: '16px' }}>
                <div style={{
                  height: '20px',
                  background: 'rgba(255,255,255,0.15)',
                  borderRadius: '4px',
                  marginBottom: '8px',
                  width: '70%'
                }} />
                <div style={{
                  height: '16px',
                  background: 'rgba(255,255,255,0.1)',
                  borderRadius: '4px',
                  width: '40%'
                }} />
              </div>
            </div>
          ))}
        </div>
        <style>{`@keyframes gallery-skeleton-shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }`}</style>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'radial-gradient(ellipse at center, rgba(26, 26, 46, 0.8) 0%, rgba(22, 33, 62, 0.8) 50%, rgba(15, 15, 35, 0.9) 100%)',
      position: 'relative',
      overflow: 'auto',
      overflowX: 'hidden',
      color: 'white',
      padding: window.innerWidth <= 768 ? '80px 16px 24px 16px' : '80px 24px 24px 24px',
      width: '100%',
      boxSizing: 'border-box'
    }}>
      {/* Psychedelic Gallery Background */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 0,
        pointerEvents: 'none'
      }}>
        {/* Floating Geometric Shapes */}
        {[
          { size: 120, left: 15, top: 20, rotation: 0, delay: 0 },
          { size: 90, left: 85, top: 15, rotation: 45, delay: 1 },
          { size: 150, left: 10, top: 70, rotation: 90, delay: 2 },
          { size: 110, left: 90, top: 80, rotation: 135, delay: 3 },
          { size: 130, left: 50, top: 10, rotation: 180, delay: 4 },
          { size: 100, left: 30, top: 50, rotation: 225, delay: 5 },
          { size: 125, left: 70, top: 60, rotation: 270, delay: 6 },
          { size: 115, left: 45, top: 85, rotation: 315, delay: 7 }
        ].map((shape, i) => (
          <div
            key={`shape-${i}`}
            style={{
              position: 'absolute',
              width: `${shape.size}px`,
              height: `${shape.size}px`,
              left: `${shape.left}%`,
              top: `${shape.top}%`,
              background: `linear-gradient(45deg, 
                hsla(${(i * 45 + 180) % 360}, 80%, 70%, 0.6), 
                hsla(${(i * 45 + 270) % 360}, 80%, 70%, 0.4)
              )`,
              borderRadius: i % 3 === 0 ? '50%' : i % 3 === 1 ? '20%' : '0%',
              animation: `floatShape ${8 + i * 2}s ease-in-out infinite`,
              animationDelay: `${shape.delay}s`,
              transform: `rotate(${shape.rotation}deg)`,
              filter: 'blur(0.5px)'
            }}
          />
        ))}
        
        {/* Subtle Color Orbs */}
        {[
          { size: 150, left: 20, top: 30, delay: 0 },
          { size: 220, left: 80, top: 25, delay: 1 },
          { size: 180, left: 5, top: 75, delay: 2 },
          { size: 250, left: 75, top: 85, delay: 3 },
          { size: 170, left: 40, top: 5, delay: 4 },
          { size: 200, left: 60, top: 45, delay: 5 }
        ].map((orb, i) => (
          <div
            key={`orb-${i}`}
            style={{
              position: 'absolute',
              width: `${orb.size}px`,
              height: `${orb.size}px`,
              left: `${orb.left}%`,
              top: `${orb.top}%`,
              background: `radial-gradient(circle, 
                hsla(${(i * 60) % 360}, 80%, 80%, 0.5), 
                transparent 70%
              )`,
              borderRadius: '50%',
              animation: `pulseOrb ${6 + i * 1.5}s ease-in-out infinite`,
              animationDelay: `${orb.delay}s`,
              filter: 'blur(1px)'
            }}
          />
        ))}
        
        {/* Subtle Grid Pattern */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundImage: `
            linear-gradient(rgba(255,255,255,0.02) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px',
          animation: 'gridShift 20s linear infinite',
          opacity: 0.3
        }} />
      </div>
      
      {/* CSS Animations */}
      <style>{`
        @keyframes floatShape {
          0%, 100% { 
            transform: translateY(0px) rotate(0deg) scale(1);
            opacity: 0.7;
          }
          25% { 
            transform: translateY(-20px) rotate(90deg) scale(1.1);
            opacity: 0.9;
          }
          50% { 
            transform: translateY(-10px) rotate(180deg) scale(0.9);
            opacity: 0.8;
          }
          75% { 
            transform: translateY(-30px) rotate(270deg) scale(1.05);
            opacity: 0.85;
          }
        }
        
        @keyframes pulseOrb {
          0%, 100% { 
            transform: scale(1);
            opacity: 0.5;
          }
          50% { 
            transform: scale(1.2);
            opacity: 0.7;
          }
        }
        
        @keyframes gridShift {
          0% { transform: translate(0, 0); }
          100% { transform: translate(50px, 50px); }
        }
        
        @keyframes pulse {
          0%, 100% { 
            transform: scale(1);
            opacity: 1;
          }
          50% { 
            transform: scale(1.1);
            opacity: 0.8;
          }
        }
      `}</style>
      <div style={{
        maxWidth: '1400px',
        margin: '0 auto',
        position: 'relative',
        zIndex: 10
      }}>
        <h1 style={{
          fontSize: '3.5rem',
          fontWeight: 'bold',
          textAlign: 'center',
          marginBottom: '16px',
          background: 'linear-gradient(45deg, #ec4899, #8b5cf6, #06b6d4)',
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          textShadow: '2px 2px 4px rgba(0,0,0,0.8)'
        }}>
          Gallery
        </h1>
        <p style={{
          fontSize: '1.25rem',
          color: '#d1d5db',
          textAlign: 'center',
          marginBottom: '16px',
          textShadow: '1px 1px 2px rgba(0,0,0,0.8)'
        }}>
          Explore our psychedelic collection
        </p>
        <p style={{
          fontSize: '1rem',
          color: '#9ca3af',
          textAlign: 'center',
          marginBottom: '32px',
          textShadow: '1px 1px 2px rgba(0,0,0,0.8)',
          fontStyle: 'italic'
        }}>
          Your opportunity to own a piece of our story
        </p>

      {/* Search and Filter */}
        <div style={{
          maxWidth: '800px',
          margin: '0 auto 32px auto',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div style={{ 
            position: 'relative', 
            width: '100%',
            maxWidth: '500px'
          }}>
          <input
            type="text"
            placeholder="Search products..."
              style={{
                width: '100%',
                padding: '12px 16px 12px 40px',
                background: 'rgba(0, 0, 0, 0.7)',
                border: '2px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '12px',
                color: 'white',
                fontSize: '16px',
                transition: 'all 0.3s ease',
                outline: 'none',
                boxSizing: 'border-box'
              }}
            value={searchTerm}
            onChange={(e) => {
              logger.logFormInput('searchTerm', e.target.value, 'Gallery');
              setSearchTerm(e.target.value);
              
              // Clear previous timeout
              if (searchTimeout) {
                clearTimeout(searchTimeout);
              }
              
              // Debounce search tracking - only track after 500ms of no typing
              const timeout = setTimeout(() => {
                if (e.target.value.length > 2) {
                  analyticsService.trackSearch(e.target.value, filteredProducts.length);
                }
              }, 500);
              
              setSearchTimeout(timeout);
            }}
              onFocus={(e) => {
                e.target.style.borderColor = '#ec4899';
                e.target.style.boxShadow = '0 0 20px rgba(236, 72, 153, 0.3)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                e.target.style.boxShadow = 'none';
              }}
            />
            <Search style={{
              position: 'absolute',
              left: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              width: '20px',
              height: '20px',
              color: '#9ca3af'
            }} />
        </div>
        <select
            style={{
              padding: '12px 16px',
              background: 'rgba(0, 0, 0, 0.7)',
              border: '2px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '12px',
              color: 'white',
              fontSize: '16px',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              outline: 'none',
              width: '200px',
              minWidth: '180px'
            }}
          value={filterCategory}
            onChange={(e) => {
              logger.logFormInput('filterCategory', e.target.value, 'Gallery');
              setFilterCategory(e.target.value);
              setActiveAttrFilters({}); // Reset attribute filters when category changes
              analyticsService.trackFilter('category', e.target.value);
            }}
            onFocus={(e) => {
              e.target.style.borderColor = '#8b5cf6';
              e.target.style.boxShadow = '0 0 20px rgba(139, 92, 246, 0.3)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = 'rgba(255, 255, 255, 0.2)';
              e.target.style.boxShadow = 'none';
            }}
        >
          <option value="all">All Categories</option>
          <option value="Food & Beverage">Food & Beverage</option>
          <option value="Apparel">Apparel</option>
          <option value="Electronics">Electronics</option>
          <option value="Beauty & Personal Care">Beauty & Personal Care</option>
          <option value="Home & Living">Home & Living</option>
          <option value="Art">Art</option>
          <option value="Accessories">Accessories</option>
          <option value="Other">Other</option>
        </select>

        {/* Toggle attribute filter panel (only if a category with dynamic fields is selected) */}
        {filterCategory !== 'all' && CATEGORY_ID_MAP[filterCategory] && (
          <button
            onClick={() => setShowFilters(f => !f)}
            style={{
              padding: '12px 20px',
              background: showFilters ? 'rgba(236,72,153,0.2)' : 'rgba(0,0,0,0.7)',
              border: `2px solid ${showFilters ? '#ec4899' : 'rgba(255,255,255,0.2)'}`,
              borderRadius: '12px',
              color: 'white',
              fontSize: '14px',
              cursor: 'pointer',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            🔍 {showFilters ? 'Hide Filters' : 'More Filters'}
            {Object.values(activeAttrFilters).filter(v => v !== undefined && v !== null && v !== false && !(Array.isArray(v) && v.length === 0)).length > 0 && (
              <span style={{ background: '#ec4899', color: 'white', borderRadius: '10px', padding: '2px 8px', fontSize: '11px' }}>
                {Object.values(activeAttrFilters).filter(v => v !== undefined && v !== null && v !== false && !(Array.isArray(v) && v.length === 0)).length}
              </span>
            )}
          </button>
        )}
      </div>

      {/* Dynamic Attribute Filter Panel */}
      {showFilters && filterCategory !== 'all' && CATEGORY_ID_MAP[filterCategory] && (
        <div style={{ maxWidth: '1200px', margin: '0 auto 24px auto' }}>
          <DynamicFilters
            categoryId={CATEGORY_ID_MAP[filterCategory]}
            activeFilters={activeAttrFilters}
            onFilterChange={(fieldName, value) =>
              setActiveAttrFilters(prev => ({ ...prev, [fieldName]: value }))
            }
            onClearAll={() => setActiveAttrFilters({})}
          />
        </div>
      )}

      {/* Items Grid (Products and Collections) */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: '24px',
          maxWidth: '1200px',
          margin: '0 auto'
        }}>
        {allItems.length > 0 ? (
          allItems.map((item) => {
            // Render Collection Card
            if (item.type === 'collection') {
              const collection = item.data;
              return (
                <Link
                  to={`/collection/${collection.name.toLowerCase().replace(/\s+/g, '-')}`}
                  key={collection.id}
                  style={{ textDecoration: 'none', display: 'block' }}
                  onClick={() => {
                    logger.log('info', 'navigation', 'Collection clicked', {
                      collectionId: collection.id,
                      collectionName: collection.name
                    });
                    analyticsService.trackEvent('collection_view', {
                      collectionId: collection.id,
                      collectionName: collection.name,
                      source: 'gallery'
                    });
                  }}
                >
                  <div style={{
                    background: 'rgba(0, 0, 0, 0.8)',
                    backdropFilter: 'blur(10px)',
                    borderRadius: '16px',
                    overflow: 'hidden',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                    border: '2px solid rgba(139, 92, 246, 0.3)',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    position: 'relative'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.05)';
                    e.currentTarget.style.boxShadow = '0 12px 40px rgba(139, 92, 246, 0.5)';
                    e.currentTarget.style.borderColor = 'rgba(139, 92, 246, 0.6)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.boxShadow = '0 8px 32px rgba(0, 0, 0, 0.3)';
                    e.currentTarget.style.borderColor = 'rgba(139, 92, 246, 0.3)';
                  }}
                  >
                    {collection.imageUrl ? (
                      <div style={{
                        width: '100%',
                        height: '200px',
                        overflow: 'hidden',
                        position: 'relative'
                      }}>
                        <img
                          src={collection.imageUrl}
                          alt={collection.name}
                          loading="lazy"
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            transition: 'transform 0.3s ease'
                          }}
                          onMouseEnter={(e) => e.target.style.transform = 'scale(1.1)'}
                          onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                          onError={(e) => {
                            e.target.src = PLACEHOLDER_IMAGE;
                          }}
                        />
                      </div>
                    ) : (
                      <div style={{
                        width: '100%',
                        height: '200px',
                        background: 'linear-gradient(135deg, #8b5cf6, #ec4899)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '3rem'
                      }}>
                        🎨
                      </div>
                    )}
                    <div style={{ padding: '20px', position: 'relative' }}>
                      <div style={{
                        position: 'absolute',
                        top: '-12px',
                        right: '20px',
                        background: 'rgba(139, 92, 246, 0.9)',
                        color: 'white',
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '600',
                        textTransform: 'uppercase'
                      }}>
                        Collection
                      </div>
                      <EyeCatchingHeading 
                        title={collection.name} 
                        styleType={collection.id ? `${collection.id}-${collection.name}` : collection.name}
                        className="mb-2"
                      />
                      {collection.shortDescription && (
                        <p style={{
                          color: '#9ca3af',
                          fontSize: '14px',
                          lineHeight: '1.4',
                          margin: 0,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden'
                        }}>
                          {collection.shortDescription}
                        </p>
                      )}
                      <div style={{
                        marginTop: '12px',
                        padding: '8px',
                        background: 'rgba(139, 92, 246, 0.1)',
                        borderRadius: '6px',
                        fontSize: '13px',
                        color: '#c4b5fd',
                        textAlign: 'center'
                      }}>
                        View Collection →
                      </div>
                    </div>
                  </div>
                </Link>
              );
            }
            
            // Render Product Card
            const product = item.data;
            const isSubscription = product.subscription || product.type === 'subscription';
            
            return (
              <Link 
                to={isSubscription ? `/product/mystery-test-subscription` : productDetailPath(product)} 
                key={product.id} 
                style={{ textDecoration: 'none', display: 'block' }}
                onClick={() => {
                  logger.logProductView(product.id, product.name, {
                    category: product.category,
                    price: product.price,
                    isSubscription: isSubscription
                  });
                  // Track product click for CTR analysis
                  analyticsService.trackProductClick(product, 'gallery');
                }}
              >
                <div style={{
                  background: isSubscription 
                    ? 'linear-gradient(135deg, rgba(139, 92, 246, 0.3), rgba(236, 72, 153, 0.3))' 
                    : 'rgba(0, 0, 0, 0.8)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: '16px',
                  overflow: 'hidden',
                  boxShadow: isSubscription 
                    ? '0 8px 32px rgba(139, 92, 246, 0.5)' 
                    : '0 8px 32px rgba(0, 0, 0, 0.3)',
                  border: isSubscription 
                    ? '2px solid rgba(139, 92, 246, 0.6)' 
                    : '1px solid rgba(255, 255, 255, 0.1)',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  position: 'relative'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.05)';
                  e.currentTarget.style.boxShadow = isSubscription 
                    ? '0 12px 40px rgba(139, 92, 246, 0.7)' 
                    : '0 12px 40px rgba(236, 72, 153, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = isSubscription 
                    ? '0 8px 32px rgba(139, 92, 246, 0.5)' 
                    : '0 8px 32px rgba(0, 0, 0, 0.3)';
                }}
                >
                  {/* Subscription Badge */}
                  {isSubscription && (
                    <div style={{
                      position: 'absolute',
                      top: '12px',
                      right: '12px',
                      background: 'linear-gradient(135deg, #8b5cf6, #ec4899)',
                      color: 'white',
                      padding: '6px 12px',
                      borderRadius: '20px',
                      fontSize: '11px',
                      fontWeight: 'bold',
                      textTransform: 'uppercase',
                      zIndex: 10,
                      boxShadow: '0 4px 12px rgba(139, 92, 246, 0.5)'
                    }}>
                      ✨ Subscription
                    </div>
                  )}
                  
                  {(() => {
                    // For subscription products, show the Rainbow Sails logo
                    if (isSubscription) {
                      return (
                        <div style={{
                          width: '100%',
                          height: '200px',
                          background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.4), rgba(236, 72, 153, 0.4))',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          position: 'relative',
                          overflow: 'hidden'
                        }}>
                          <img
                            src="/favicon.png"
                            alt="Rainbow Sails Subscription"
                            style={{
                              width: '120px',
                              height: '120px',
                              objectFit: 'contain',
                              animation: 'pulse 2s ease-in-out infinite',
                              filter: 'drop-shadow(0 0 20px rgba(255, 255, 255, 0.5))'
                            }}
                            onError={(e) => {
                              e.target.src = '/Logo.png';
                            }}
                          />
                          <div style={{
                            position: 'absolute',
                            bottom: '8px',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            background: 'rgba(0, 0, 0, 0.6)',
                            color: 'white',
                            padding: '4px 12px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: 'bold',
                            textTransform: 'uppercase'
                          }}>
                            Zee Mystery Subscription Test
                          </div>
                        </div>
                      );
                    }
                    
                    // Priority: primaryImage > images[0] > customImage > folder > Printful > placeholder
                    let imageUrl = null;
                    
                    // Use Mango product images first (already full URLs from productsService)
                    if (product.primaryImage) {
                      imageUrl = product.primaryImage;
                    } else if (product.images && product.images.length > 0) {
                      imageUrl = product.images[0];
                    } else if (product.customImage) {
                      imageUrl = product.customImage;
                    } else if (product.folder) {
                      // Legacy folder-based system
                      imageUrl = `/products/${product.folder}/custom-0.jpg`;
                    } else if (product.printfulData?.designThumbnailUrl) {
                      imageUrl = product.printfulData.designThumbnailUrl;
                    } else {
                      imageUrl = PLACEHOLDER_IMAGE;
                    }
                    
                    return (
                      <div style={{
                        width: '100%',
                        aspectRatio: '4/3',
                        overflow: 'hidden',
                        position: 'relative'
                      }}>
                        <img
                          src={imageUrl}
                          alt={product.name}
                          loading="lazy"
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            transition: 'transform 0.3s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.transform = 'scale(1.1)';
                            if (imageUrl && !imageUrl.startsWith('data:')) {
                              const preload = document.createElement('link');
                              preload.rel = 'preload';
                              preload.as = 'image';
                              preload.href = imageUrl;
                              document.head.appendChild(preload);
                              setTimeout(() => document.head.removeChild(preload), 5000);
                            }
                          }}
                          onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                          onError={(e) => {
                            console.error('❌ [Gallery] Image failed to load:', e.target.src);
                            // Fallback chain for Mango products
                            if (product.images && product.images.length > 1) {
                              const currentIndex = product.images.indexOf(e.target.src);
                              if (currentIndex !== -1 && currentIndex < product.images.length - 1) {
                                e.target.src = product.images[currentIndex + 1];
                                return;
                              }
                            }
                            
                            // Legacy folder fallbacks
                            if (product.folder) {
                              if (e.target.src.includes('custom-0.jpg')) {
                                e.target.src = `/products/${product.folder}/variant-0-default-0.png`;
                                return;
                              } else if (e.target.src.includes('variant-0-default-0.png')) {
                                e.target.src = product.printfulData?.designThumbnailUrl || PLACEHOLDER_IMAGE;
                                return;
                              }
                            }
                            
                            // Final fallbacks
                            if (product.printfulData?.designThumbnailUrl) {
                              e.target.src = product.printfulData.designThumbnailUrl;
                              return;
                            }
                            
                            // Use placeholder constant
                            if (e.target.src !== PLACEHOLDER_IMAGE) {
                              e.target.src = PLACEHOLDER_IMAGE;
                            }
                          }}
                        />
                      </div>
                    );
                  })()}
                  <div style={{ padding: '20px' }}>
                    {/* Product type badges */}
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '8px' }}>
                      {product.productType === 'print_on_demand' && (
                        <span style={{
                          display: 'inline-block',
                          padding: '2px 8px',
                          borderRadius: '8px',
                          fontSize: '10px',
                          fontWeight: '700',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          background: 'rgba(139,92,246,0.15)',
                          border: '1px solid rgba(139,92,246,0.35)',
                          color: '#c4b5fd'
                        }}>
                          Print on Demand
                        </span>
                      )}
                      {product.isOnSale && (
                        <span style={{
                          display: 'inline-block',
                          padding: '2px 8px',
                          borderRadius: '8px',
                          fontSize: '10px',
                          fontWeight: '700',
                          textTransform: 'uppercase',
                          background: 'rgba(16,185,129,0.15)',
                          border: '1px solid rgba(16,185,129,0.35)',
                          color: '#6ee7b7'
                        }}>
                          Sale
                        </span>
                      )}
                    </div>

                    <EyeCatchingHeading 
                      title={product.name} 
                      styleType={product.id ? `${product.id}-${product.name}` : product.name}
                      className="mb-2"
                    />

                    {/* Price — show range when product has variants */}
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px', marginBottom: '8px', flexWrap: 'wrap' }}>
                      {(() => {
                        if (product.productType === 'donation') {
                          const cfg = product.pricingConfig || {};
                          const suggested = Array.isArray(cfg.suggestedAmounts) && cfg.suggestedAmounts.length > 0
                            ? cfg.suggestedAmounts
                            : null;
                          const fromAmount = suggested
                            ? Math.min(...suggested)
                            : (cfg.minimum > 0 ? cfg.minimum : null);
                          const goal = cfg.goal ? Number(cfg.goal) : null;
                          const raised = product.donatedTotal ? Number(product.donatedTotal) : 0;
                          const pct = goal && goal > 0 ? Math.min(100, Math.round((raised / goal) * 100)) : null;
                          return (
                            <div style={{ width: '100%' }}>
                              <span style={{ color: '#f9a8d4', fontSize: '15px', fontWeight: '700' }}>
                                {fromAmount ? `Donate from $${fromAmount}` : 'Make a donation'}
                              </span>
                              {cfg.showProgress && goal && (
                                <div style={{ marginTop: '6px' }}>
                                  <div style={{ background: 'rgba(255,255,255,0.12)', borderRadius: '4px', height: '6px', overflow: 'hidden' }}>
                                    <div style={{ width: `${pct}%`, height: '100%', background: 'linear-gradient(90deg, #ec4899, #8b5cf6)', borderRadius: '4px', transition: 'width 0.5s ease' }} />
                                  </div>
                                  <p style={{ color: '#9ca3af', fontSize: '11px', margin: '3px 0 0 0' }}>
                                    ${raised.toFixed(0)} raised of ${goal.toLocaleString()} goal
                                  </p>
                                </div>
                              )}
                            </div>
                          );
                        }
                        if (isSubscription) {
                          return (
                            <>
                              <PriceDisplay price={product.price} className="text-lg font-bold" />
                              <span style={{ color: '#c4b5fd', fontSize: '13px' }}>/month</span>
                            </>
                          );
                        }
                        const variants = product.variants;
                        if (variants && variants.length > 1) {
                          const prices = variants
                            .map(v => parseFloat(v.price || 0))
                            .filter(p => !isNaN(p) && p > 0);
                          if (prices.length > 0) {
                            const minP = Math.min(...prices);
                            const maxP = Math.max(...prices);
                            return (
                              <>
                                <PriceDisplay price={minP} className="text-lg font-bold" />
                                {maxP > minP && (
                                  <>
                                    <span style={{ color: '#6b7280', fontSize: '13px' }}>–</span>
                                    <PriceDisplay price={maxP} className="text-lg font-bold" />
                                  </>
                                )}
                              </>
                            );
                          }
                        }
                        const displayPrice = product.isOnSale && product.salePrice != null
                          ? product.salePrice
                          : product.price;
                        return (
                          <>
                            <PriceDisplay price={displayPrice} className="text-lg font-bold" />
                            {product.isOnSale && product.originalPrice != null && (
                              <span style={{ textDecoration: 'line-through', color: '#6b7280', fontSize: '13px' }}>
                                <PriceDisplay price={product.originalPrice} />
                              </span>
                            )}
                          </>
                        );
                      })()}
                    </div>

                    {/* Variant count hint */}
                    {product.variants && product.variants.length > 1 && !isSubscription && (() => {
                      const colors = [...new Set(product.variants.map(v => v.color || v.option2Value).filter(Boolean))];
                      const sizes  = [...new Set(product.variants.map(v => v.size  || v.option1Value).filter(Boolean))];
                      const parts  = [
                        colors.length > 1 && `${colors.length} colours`,
                        sizes.length  > 1 && `${sizes.length} sizes`,
                      ].filter(Boolean);
                      if (!parts.length) return null;
                      return (
                        <p style={{ color: '#6b7280', fontSize: '12px', margin: '0 0 8px 0' }}>
                          {parts.join(' · ')}
                        </p>
                      );
                    })()}
                    
                    {/* Stock Quantity Display */}
                    {(() => {
                      const stockQty = product.trackInventory && product.inventoryQty !== undefined ? product.inventoryQty : null;
                      if (stockQty === null || stockQty === 0) return null;
                      return (
                        <div style={{ marginBottom: '8px', textAlign: 'center' }}>
                          <span style={{
                            display: 'inline-block',
                            padding: '4px 12px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: '600',
                            background: stockQty === 1 
                              ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' 
                              : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                            color: 'white',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                          }}>
                            {stockQty === 1 ? '1 left' : `${stockQty} spots available`}
                          </span>
                        </div>
                      );
                    })()}
                    
                    {(product.shortDescription || product.description) && (
                      <p style={{
                        color: isSubscription ? '#e9d5ff' : '#9ca3af',
                        fontSize: '14px',
                        lineHeight: '1.5',
                        margin: 0,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}>
                        {product.shortDescription || product.description}
                      </p>
                    )}
                  </div>
                </div>
              </Link>
            );
          })
        ) : (
            <div style={{
              gridColumn: '1 / -1',
              textAlign: 'center',
              padding: '60px 20px',
              color: '#9ca3af'
            }}>
              <div style={{
                fontSize: '4rem',
                marginBottom: '16px'
              }}>
                🎨
              </div>
              <h3 style={{
                fontSize: '1.5rem',
                fontWeight: '600',
                marginBottom: '8px',
                color: '#d1d5db'
              }}>
                No products found
              </h3>
              <p style={{
                fontSize: '1rem',
                color: '#9ca3af'
              }}>
                Try adjusting your search or filter criteria
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Gallery;
